import { useCallback, useMemo, useState } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts'

import {
  CHART_ACTIVE_DOT,
  CHART_ANIMATION,
  CHART_AXIS_LINE,
  CHART_AXIS_TICK,
  CHART_GRID_PROPS,
  CHART_MARGIN,
  CHART_TOOLTIP_STYLE
} from '../../../Components/Charts/chartTheme'
import { cap } from '../../../State'
import { translations } from '../../../translations'

const language = cap.language.state || 'IT'
const t = translations[language]

const BIN_OPTIONS = [
  { key: '1h', label: '1h', ms: 3600000 },
  { key: '3h', label: '3h', ms: 3 * 3600000 },
  { key: '6h', label: '6h', ms: 6 * 3600000 },
  { key: '12h', label: '12h', ms: 12 * 3600000 },
  { key: '1d', label: '1g', ms: 86400000 }
]

/**
 * Calcola automaticamente la dimensione del bin in base al range di date
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {string} Chiave del bin ottimale
 */
function autoBinSize(startDate, endDate) {
  const hours = (endDate.getTime() - startDate.getTime()) / 3600000
  if (hours <= 6) return '1h'
  if (hours <= 48) return '3h'
  if (hours <= 96) return '6h'
  if (hours <= 168) return '12h'
  return '1d'
}

/**
 * Formatta il timestamp del bin in base alla sua dimensione
 * @param {number} timestamp - Timestamp Unix in ms
 * @param {number} binMs - Durata del bin in ms
 * @returns {string} Etichetta formattata
 */
function formatBinLabel(timestamp, binMs) {
  const d = new Date(timestamp)
  const dd = d.getDate().toString().padStart(2, '0')
  const mm = (d.getMonth() + 1).toString().padStart(2, '0')
  const hh = d.getHours().toString().padStart(2, '0')
  const min = d.getMinutes().toString().padStart(2, '0')
  if (binMs >= 86400000) return `${dd}/${mm}`
  return `${dd}/${mm} ${hh}:${min}`
}

/**
 * Grafico ad area che mostra il numero di processi in bin di tempo configurabili.
 * Preferisce hourlyFromCache (righe processOverviewHourly); altrimenti deriva da processes.
 * @param {{ processes?: Array<{ createdAt: string }>, hourlyFromCache?: Array<{ bucketIso: string, count: number }>, totalFromCache?: number, startDate: Date, endDate: Date }} props
 * @returns {JSX.Element|null}
 */
export const ProcessCountChart = ({
  processes = [],
  hourlyFromCache = null,
  totalFromCache,
  startDate,
  endDate
}) => {
  const [binSize, setBinSize] = useState(() => autoBinSize(startDate, endDate))

  const handleBinChange = useCallback((key) => setBinSize(key), [])

  const chartData = useMemo(() => {
    if (!startDate || !endDate) return []
    const binMs = BIN_OPTIONS.find((b) => b.key === binSize)?.ms || 3600000
    const start = startDate.getTime()
    const end = endDate.getTime()

    const bins = {}
    for (let t = start; t < end; t += binMs) {
      bins[t] = 0
    }

    if (hourlyFromCache?.length) {
      for (const row of hourlyFromCache) {
        const t = new Date(row.bucketIso).getTime()
        if (t < start || t > end) continue
        const binStart = start + Math.floor((t - start) / binMs) * binMs
        bins[binStart] = (bins[binStart] || 0) + (row.count || 0)
      }
    } else if (processes.length) {
      for (const p of processes) {
        const ts = new Date(p.createdAt).getTime()
        if (ts < start || ts > end) continue
        const binStart = start + Math.floor((ts - start) / binMs) * binMs
        bins[binStart] = (bins[binStart] || 0) + 1
      }
    }

    return Object.entries(bins)
      .map(([ts, count]) => ({
        timestamp: parseInt(ts),
        name: formatBinLabel(parseInt(ts), binMs),
        count
      }))
      .sort((a, b) => a.timestamp - b.timestamp)
  }, [processes, hourlyFromCache, startDate, endDate, binSize])

  const totalProcesses =
    totalFromCache != null
      ? totalFromCache
      : hourlyFromCache?.length
        ? hourlyFromCache.reduce((a, r) => a + (r.count || 0), 0)
        : processes.length
  const maxCount = chartData.length > 0 ? Math.max(...chartData.map((d) => d.count)) : 0
  const nonZeroBins = chartData.filter((d) => d.count > 0).length
  const avgCount = nonZeroBins > 0 ? (totalProcesses / nonZeroBins).toFixed(1) : 0

  if (!totalProcesses && !chartData.some((d) => d.count > 0)) return null

  return (
    <div className="ncard">
      <div className="ncard__head">
        <div className="ncard__ico">
          <i className="pi pi-chart-bar" style={{ fontSize: 16 }} />
        </div>
        <div>
          <h3 className="ncard__title">{t.pfProcessiCreatiPerOra}</h3>
          <div className="ncard__sub">
            {t.pfTotaleCache}: {totalProcesses} · {t.pfMediaBin}: {avgCount} · Max: {maxCount}
          </div>
        </div>
        <div className="ncard__head-actions">
          {BIN_OPTIONS.map((b) => (
            <button
              key={b.key}
              type="button"
              className={`npill ${binSize === b.key ? 'is-active' : ''}`}
              onClick={() => handleBinChange(b.key)}
              style={{ height: 28, fontSize: 11, padding: '0 10px' }}
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>
      <div className="ncard__body">
        <div style={{ height: 280 }}>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ ...CHART_MARGIN, right: 16, bottom: 8 }}>
                <defs>
                  <linearGradient id="processCountGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--primary)" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="var(--primary)" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid {...CHART_GRID_PROPS} />
                <XAxis
                  dataKey="name"
                  tick={CHART_AXIS_TICK}
                  axisLine={CHART_AXIS_LINE}
                  tickLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={CHART_AXIS_TICK}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  formatter={(value) => [value, t.pfProcessi]}
                  labelStyle={{ fontWeight: 600, marginBottom: 2 }}
                  contentStyle={CHART_TOOLTIP_STYLE}
                />
                <Area
                  type="monotone"
                  dataKey="count"
                  name={t.pfProcessi}
                  stroke="var(--primary)"
                  strokeWidth={2.5}
                  fill="url(#processCountGradient)"
                  activeDot={CHART_ACTIVE_DOT('var(--primary)')}
                  animationDuration={CHART_ANIMATION.duration}
                  animationEasing={CHART_ANIMATION.easing}
                />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div
              style={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--ink-400)'
              }}
            >
              {t.pfNessunDatoNelPeriodo}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
